import { AttributesSelector } from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useFormContext } from 'react-hook-form'
import { FiltersSchema } from '../filtersSchema'
import { CompanyIdsFilter } from './CompanyIdsFilter'
import { CompanyGroupIdsFilter } from './CompanyGroupIdsFilter'
import { SalesOwnersFilter } from './SalesOwnersFilter'
import { AccountOwnersFilter } from './AccountOwnersFilter'
import { companyTypes } from './CompanyTypes/constants'
import { CompanyTypesFilter } from './CompanyTypes/CompanyTypesFilter'

export const CompanyFilters = () => {
  const methods = useFormContext<FiltersSchema>()

  const company = methods.watch('company')

  const resetToDefaultValues = () => {
    methods.reset({
      ...methods.getValues(),
      company: {
        companyGroupIds: [],
        companyIds: [],
        salesOwnerIds: [],
        accountOwnerIds: [],
        companyTypes: [],
      },
    })
  }

  const resetCompanyIds = () => {
    methods.reset({
      ...methods.getValues(),
      company: {
        ...methods.getValues('company'),
        companyIds: [],
      },
    })
  }

  const resetCompanyGroupIds = () => {
    methods.reset({
      ...methods.getValues(),
      company: {
        ...methods.getValues('company'),
        companyGroupIds: [],
      },
    })
  }

  const resetSalesOwnerIds = () => {
    methods.reset({
      ...methods.getValues(),
      company: { ...methods.getValues('company'), salesOwnerIds: [] },
    })
  }

  const resetAccountOwnerIds = () => {
    methods.reset({
      ...methods.getValues(),
      company: { ...methods.getValues('company'), accountOwnerIds: [] },
    })
  }

  const resetCompanyTypes = () => {
    methods.reset({
      ...methods.getValues(),
      company: { ...methods.getValues('company'), companyTypes: [] },
    })
  }

  const numberOfCompanyIdsSelected = useMemo(() => {
    return company.companyIds?.length || 0
  }, [company.companyIds])

  const numberOfCompanyGroupIdsSelected = useMemo(() => {
    return company.companyGroupIds?.length || 0
  }, [company.companyGroupIds])

  const numberOfSalesOwnerIdsSelected = useMemo(() => {
    return company.salesOwnerIds?.length || 0
  }, [company.salesOwnerIds])

  const numberOfAccountOwnerIdsSelected = useMemo(() => {
    return company.accountOwnerIds?.length || 0
  }, [company.accountOwnerIds])

  const numberOfCompanyTypesSelected = useMemo(() => {
    return company.companyTypes?.length || 0
  }, [company.companyTypes])

  const selectedCompanyTypes = useMemo(() => {
    if (numberOfCompanyTypesSelected === 0) {
      return []
    }

    if (numberOfCompanyTypesSelected === 1) {
      const foundStatus = companyTypes.find(
        (status) => status.id === company.companyTypes?.[0]
      )
      if (foundStatus) {
        return [foundStatus.label]
      }
    }

    if (numberOfCompanyTypesSelected > 1) {
      const pluralRules = new Intl.PluralRules('en-US')
      const plural = pluralRules.select(numberOfCompanyTypesSelected)
      const suffix = plural === 'one' ? 'Company Type' : 'Company Types'
      return [`${numberOfCompanyTypesSelected} ${suffix}`]
    }

    return []
  }, [numberOfCompanyTypesSelected, company.companyTypes])

  const selectedAttributes = useMemo(() => {
    const fields: string[] = []
    const pluralRules = new Intl.PluralRules('en-US')

    if (numberOfCompanyIdsSelected > 0) {
      const plural = pluralRules.select(numberOfCompanyIdsSelected)
      const suffix = plural === 'one' ? 'Company' : 'Companies'
      fields.push(`${numberOfCompanyIdsSelected} ${suffix}`)
    }

    if (numberOfCompanyGroupIdsSelected > 0) {
      const plural = pluralRules.select(numberOfCompanyGroupIdsSelected)
      const suffix = plural === 'one' ? 'Company Group' : 'Company Groups'
      fields.push(`${numberOfCompanyGroupIdsSelected} ${suffix}`)
    }

    if (numberOfSalesOwnerIdsSelected > 0) {
      const plural = pluralRules.select(numberOfSalesOwnerIdsSelected)
      const suffix = plural === 'one' ? 'Sales Owner' : 'Sales Owners'
      fields.push(`${numberOfSalesOwnerIdsSelected} ${suffix}`)
    }

    if (numberOfAccountOwnerIdsSelected > 0) {
      const plural = pluralRules.select(numberOfAccountOwnerIdsSelected)
      const suffix = plural === 'one' ? 'Account Owner' : 'Account Owners'
      const accountOwnerText = `${numberOfAccountOwnerIdsSelected} ${suffix}`
      fields.push(accountOwnerText)
    }

    if (numberOfCompanyTypesSelected > 0) {
      fields.push(...selectedCompanyTypes)
    }

    return fields
  }, [
    numberOfCompanyIdsSelected,
    numberOfCompanyGroupIdsSelected,
    numberOfSalesOwnerIdsSelected,
    numberOfAccountOwnerIdsSelected,
    numberOfCompanyTypesSelected,
    selectedCompanyTypes,
  ])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="Company"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        selectedAttributeLimit={5}
        groups={[
          <AttributesSelector.Group
            key="company-ids"
            title="Company"
            numberOfSelectedAttributes={numberOfCompanyIdsSelected}
            onClearAll={resetCompanyIds}
          >
            <CompanyIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="company-group-ids"
            title="Company Group"
            numberOfSelectedAttributes={numberOfCompanyGroupIdsSelected}
            onClearAll={resetCompanyGroupIds}
          >
            <CompanyGroupIdsFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="company-types"
            title="Company Types"
            numberOfSelectedAttributes={numberOfCompanyTypesSelected}
            onClearAll={resetCompanyTypes}
          >
            <CompanyTypesFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="sales-owner-ids"
            title="Sales Owner"
            numberOfSelectedAttributes={numberOfSalesOwnerIdsSelected}
            onClearAll={resetSalesOwnerIds}
          >
            <SalesOwnersFilter />
          </AttributesSelector.Group>,
          <AttributesSelector.Group
            key="account-owner-ids"
            title="Account Owner"
            numberOfSelectedAttributes={numberOfAccountOwnerIdsSelected}
            onClearAll={resetAccountOwnerIds}
          >
            <AccountOwnersFilter />
          </AttributesSelector.Group>,
        ]}
      />
    </FormProvider>
  )
}
